import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import {
  toApiErrorPayload,
  type ApiErrorPayload,
} from "@/utils/apiErrorPayload";

const BaseURL = import.meta.env.VITE_API_URL;

export interface BudgetAnalysisMonthly {
  month_key: string;
  income_budget: number;
  income_actual: number;
  expense_budget: number;
  expense_actual: number;
  income_budget_entered?: boolean;
  income_actual_entered?: boolean;
  expense_budget_entered?: boolean;
  expense_actual_entered?: boolean;
}

export interface BudgetAnalysisProperty {
  property_id: string;
  name: string;
  group_name: string | null;
  monthly: BudgetAnalysisMonthly[];
}

export interface BudgetAnalysisResponse {
  fiscal_year: string;
  /** 決算月 (1〜12)。会計年度開始月は翌月。 */
  fiscal_closing_month: number;
  month_keys: string[];
  properties: BudgetAnalysisProperty[];
}

export const fetchBudgetAnalysis = createAsyncThunk<
  BudgetAnalysisResponse,
  { fiscal_year: string },
  { rejectValue: ApiErrorPayload }
>("budgetAnalysis/fetch", async ({ fiscal_year }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/budget-analysis`, {
      params: { fiscal_year },
    });
    return res.data.data as BudgetAnalysisResponse;
  } catch (error) {
    return rejectWithValue(
      toApiErrorPayload(error, "Failed to fetch budget analysis"),
    );
  }
});

interface BudgetAnalysisState {
  data: BudgetAnalysisResponse | null;
  loading: boolean;
  error: ApiErrorPayload | null;
}

const initialState: BudgetAnalysisState = {
  data: null,
  loading: false,
  error: null,
};

const budgetAnalysisSlice = createSlice({
  name: "budgetAnalysis",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBudgetAnalysis.pending, (state, action) => {
        const nextKey =
          (action.meta.arg as any)?.fiscal_year != null
            ? String((action.meta.arg as any).fiscal_year)
            : null;
        const cur: any = state;
        // 会計年度が変わったら前データをクリアして race を防ぐ
        if (cur.requestedKey !== nextKey) {
          state.data = null;
        }
        cur.requestedKey = nextKey;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBudgetAnalysis.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchBudgetAnalysis.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      });
  },
});

export default budgetAnalysisSlice.reducer;
